import { useMemo } from 'react'
import { useQuery, useQueries, type QueryClient } from '@tanstack/react-query'
import { teacherMeApi } from '../../services/api'
import type { TeacherMeSchoolYearScope } from '../../types/api'
import { localIsoDate } from '../../utils/localDate'

const DASHBOARD_STALE_TIME = 60 * 1000

export const teacherMeQueryKeys = {
  all: ['academics', 'teacher-me'] as const,
  classes: (schoolId: string) => [...teacherMeQueryKeys.all, 'classes', schoolId] as const,
  timetable: (schoolId: string, day: string, schoolYearId: string) =>
    [...teacherMeQueryKeys.all, 'timetable', schoolId, day, schoolYearId] as const,
  attention: (schoolId: string, schoolYearId: string) =>
    [...teacherMeQueryKeys.all, 'attention', schoolId, schoolYearId] as const,
  assignments: (schoolId: string, schoolYearId: string) =>
    [...teacherMeQueryKeys.all, 'assignments', schoolId, schoolYearId] as const,
  weeklyAttendance: (schoolId: string, schoolYearId: string) =>
    [...teacherMeQueryKeys.all, 'weekly-attendance', schoolId, schoolYearId] as const,
}

/** Use from other features instead of importing `teacherMeApi` directly (no-restricted-imports). */
export function teacherMeClassesQueryOptions(schoolId: string) {
  return {
    queryKey: teacherMeQueryKeys.classes(schoolId),
    queryFn: () => teacherMeApi.listClasses({ school_id: schoolId }),
    staleTime: DASHBOARD_STALE_TIME,
  }
}

/** Days of timetable the home page loads: today and the six after it (a full week). */
export const SCHEDULE_DAYS = 7
/** Assignments due from today through this many days ahead are "coming up". */
const COMING_UP_DAYS = 7
/** How far back to look for already-due work that still needs grading (the school year bounds it). */
const GRADING_LOOKBACK_DAYS = 366

/** Local calendar dates (`YYYY-MM-DD`) starting today: the teacher's own "today". */
export function scheduleDates(from = new Date(), count = SCHEDULE_DAYS): string[] {
  return Array.from({ length: count }, (_, offset) => {
    const day = new Date(from.getFullYear(), from.getMonth(), from.getDate() + offset)
    return localIsoDate(day)
  })
}

function timetableQueryOptions(schoolId: string, day: string, schoolYearId: string) {
  return {
    queryKey: teacherMeQueryKeys.timetable(schoolId, day, schoolYearId),
    queryFn: () => teacherMeApi.timetable({ day, school_year_id: schoolYearId }),
    staleTime: DASHBOARD_STALE_TIME,
    // A date past the end of the school year is a 400, not a transient failure.
    retry: false,
  }
}

function assignmentsQueryOptions(schoolId: string, schoolYearId: string) {
  return {
    queryKey: teacherMeQueryKeys.assignments(schoolId, schoolYearId),
    queryFn: () =>
      teacherMeApi.upcomingAssignments({
        school_year_id: schoolYearId,
        days: COMING_UP_DAYS,
        past_days: GRADING_LOOKBACK_DAYS,
      }),
    staleTime: DASHBOARD_STALE_TIME,
  }
}

/**
 * Warms teacher `/me/*` dashboard queries after login when `school_year_id` is known
 * (resolved from the first class row). Safe to call from auth flows outside this hook.
 */
export async function prefetchTeacherMeDashboard(queryClient: QueryClient, schoolId: string) {
  const classes = await queryClient.fetchQuery(teacherMeClassesQueryOptions(schoolId))

  const schoolYearId = classes[0]?.school_year_id
  if (!schoolYearId) return

  const scope = { school_year_id: schoolYearId }
  const [today] = scheduleDates(new Date(), 1)

  await Promise.all([
    today ? queryClient.prefetchQuery(timetableQueryOptions(schoolId, today, schoolYearId)) : null,
    queryClient.prefetchQuery({
      queryKey: teacherMeQueryKeys.attention(schoolId, schoolYearId),
      queryFn: () => teacherMeApi.attentionList(scope),
      staleTime: DASHBOARD_STALE_TIME,
    }),
    queryClient.prefetchQuery(assignmentsQueryOptions(schoolId, schoolYearId)),
    queryClient.prefetchQuery({
      queryKey: teacherMeQueryKeys.weeklyAttendance(schoolId, schoolYearId),
      queryFn: () => teacherMeApi.weeklyAttendance(scope),
      staleTime: DASHBOARD_STALE_TIME,
    }),
  ])
}

type Options = {
  enabled?: boolean
}

/**
 * Parallel loads for the teacher home dashboard (MAK-196): `/academics/me/*` aggregates.
 * Loads `/me/classes/?school_id=` first, then uses `school_year_id` from the first class
 * for the week's timetable, attention list, assignments and weekly attendance.
 */
export function useTeacherDashboardData(schoolId: string | null, options?: Options) {
  const enabled = options?.enabled !== false && !!schoolId
  const sid = schoolId ?? 'none'

  const classesQuery = useQuery({
    ...teacherMeClassesQueryOptions(sid),
    enabled,
  })

  const schoolYearId = classesQuery.data?.[0]?.school_year_id ?? null
  const yearScope: TeacherMeSchoolYearScope | undefined = schoolYearId
    ? { school_year_id: schoolYearId }
    : undefined
  const dependentEnabled = enabled && !!yearScope
  const yid = schoolYearId ?? 'none'

  // Recomputed on each render so a dashboard left open overnight moves to the new day.
  const todayIso = localIsoDate()
  const dates = useMemo(() => scheduleDates(new Date(`${todayIso}T12:00:00`)), [todayIso])

  const timetableQueries = useQueries({
    queries: dates.map(day => ({
      ...timetableQueryOptions(sid, day, yid),
      enabled: dependentEnabled,
    })),
  })

  const [attentionQuery, assignmentsQuery, weeklyQuery] = useQueries({
    queries: [
      {
        queryKey: teacherMeQueryKeys.attention(sid, yid),
        queryFn: () => teacherMeApi.attentionList(yearScope),
        enabled: dependentEnabled,
        staleTime: DASHBOARD_STALE_TIME,
      },
      { ...assignmentsQueryOptions(sid, yid), enabled: dependentEnabled },
      {
        queryKey: teacherMeQueryKeys.weeklyAttendance(sid, yid),
        queryFn: () => teacherMeApi.weeklyAttendance(yearScope),
        enabled: dependentEnabled,
        staleTime: DASHBOARD_STALE_TIME,
      },
    ],
  })

  const scheduleDays = dates.map((date, index) => {
    const query = timetableQueries[index]
    return {
      date,
      sessions: query?.data ?? [],
      isLoading: !!query?.isLoading,
      isError: !!query?.isError,
    }
  })
  const todayQuery = timetableQueries[0]

  return {
    schoolYearId,
    todayIso,
    classes: classesQuery.data ?? [],
    classesQuery,
    scheduleDays,
    todayQuery: {
      isLoading: !!todayQuery?.isLoading,
      isError: !!todayQuery?.isError,
      refetch: () => todayQuery?.refetch(),
    },
    refetchSchedule: () => {
      for (const query of timetableQueries) if (query.isError) void query.refetch()
    },
    attentionItems: attentionQuery.data ?? [],
    attentionQuery,
    assignments: assignmentsQuery.data ?? [],
    assignmentsQuery,
    weeklyAttendance: weeklyQuery.data?.rows ?? [],
    weeklyQuery,
  }
}
